import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../util/Auth";
import TopMenu from "../../components/TopMenu/TopMenu";
import Modal from "../../components/Modal/Modal";
import Table, { TableColumn } from "../../components/Table/Table";
import ContactInfoModal from "../../components/ContactInfoModal/ContactInfoModal";
import FacilitatorCell from "../../components/FacilitatorCell/FacilitatorCell";
import PhoneIcon from "../../components/icons/PhoneIcon";
import useEntrevistas, { AssignableUser } from "./useEntrevistas";
import "./Entrevistas.css";
import { EscalaPlantao } from "../../models/Plantao";
import { formatDateTime } from "../../util/timezone.service";
import useTimezone from "../../hooks/useTimezone";
import { INTERVIEWER_CAPABILITY_NAMES, ASSISTANT_CAPABILITY_NAMES } from "../../util/permissions";
import { User } from "../../models/User";
import { getShiftStatusLabel } from "../../types/shiftStatus";

type SelfEnrollModalState = {
  escalaId: string;
  role: "interviewer" | "assistant";
  funcao: string;
  dataHorario: string;
} | null;

type AssignModalState = {
  escalaId: string;
  role: "interviewer" | "assistant";
  dataHorario: string;
} | null;

type ContactModalState = {
  userId: string;
  userName: string;
} | null;

const SEARCH_DEBOUNCE_MS = 300;

export default function Entrevistas() {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const [searchParams] = useSearchParams();
  const { user, hasPermission } = useAuth();

  const plantaoId = searchParams.get("id");
  const {
    plantao,
    enrollAsInterviewer,
    enrollAsAssistant,
    assignInterviewerToSlot,
    assignAssistantToSlot,
    removeInterviewerFromSlot,
    removeAssistantFromSlot,
    searchInterviewers,
    searchAssistants
  } = useEntrevistas(plantaoId);
  const modalidade = (plantao?.mode?.toLowerCase() ?? searchParams.get("modalidade")?.toLowerCase() ?? "online");
  const isOnlineShift = modalidade === "online";
  const modalidadeDisplay = isOnlineShift ? "ONLINE" : "Presencial";
  const localFromQuery = searchParams.get("placeName") || searchParams.get("cidadeId") || "";
  const localDoPlantao = plantao?.place_id?.name ?? plantao?.city_id?.name ?? localFromQuery;

  const [selfEnrollModal, setSelfEnrollModal] = useState<SelfEnrollModalState>(null);
  const [assignModal, setAssignModal] = useState<AssignModalState>(null);
  const [contactModal, setContactModal] = useState<ContactModalState>(null);
  const [userSearch, setUserSearch] = useState("");
  const [modalCandidates, setModalCandidates] = useState<AssignableUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (query: string, role: "interviewer" | "assistant") => {
    setSearchLoading(true);
    try {
      const results = role === "interviewer"
        ? await searchInterviewers(query)
        : await searchAssistants(query);
      setModalCandidates(results);
    } catch {
      setModalCandidates([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchInterviewers, searchAssistants]);

  useEffect(() => {
    if (!assignModal) {
      setModalCandidates([]);
      setUserSearch("");
      return;
    }
    runSearch("", assignModal.role);
  }, [assignModal?.escalaId, assignModal?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value: string) => {
    setUserSearch(value);
    if (!assignModal) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(value, assignModal.role);
    }, SEARCH_DEBOUNCE_MS);
  };

  const canSelfEnroll = (capabilityNames: string[]): boolean =>
    user.roles.some(r => capabilityNames.includes(r));

  const titulo = isOnlineShift
    ? t("pages.entrevistas.onlineTitle")
    : t("pages.entrevistas.presencialTitle", { placeName: localDoPlantao || "" });

  const handleSelfEnrollConfirm = async () => {
    if (!selfEnrollModal) return;
    if (selfEnrollModal.role === "interviewer") {
      await enrollAsInterviewer(selfEnrollModal.escalaId);
    } else {
      await enrollAsAssistant(selfEnrollModal.escalaId);
    }
    setSelfEnrollModal(null);
  };

  const handleAssignConfirm = async (selectedUser: AssignableUser) => {
    if (!assignModal) return;
    if (assignModal.role === "interviewer") {
      await assignInterviewerToSlot(assignModal.escalaId, selectedUser.id);
    } else {
      await assignAssistantToSlot(assignModal.escalaId, selectedUser.id);
    }
    setAssignModal(null);
  };

  function renderContactButton(u: User) {
    return (
      <button
        type="button"
        className="contact-icon-btn"
        title={t("pages.entrevistas.requestContactTooltip")}
        onClick={() => setContactModal({
          userId: u.id,
          userName: `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
        })}
      >
        <PhoneIcon />
      </button>
    );
  }

  const renderInterviewerCell = (entrevista: EscalaPlantao) => {
    const dateLabel = formatDateTime(entrevista?.start_date_utc, t("pages.configuration.dateTimeFormats.long"), { timezone, locale: t("current_locale") });

    if (entrevista.interviewer_user_id) {
      const name = `${entrevista.interviewer_user_id.first_name ?? ""} ${entrevista.interviewer_user_id.last_name ?? ""} (${entrevista.interviewer_user_id.cod_curso ?? ""})`;
      return (
        <span className="slot-filled">
          {name}
          {hasPermission("shift.remove_any_interviewer") && (
            <button
              type="button"
              className="remove-slot-btn"
              onClick={() => removeInterviewerFromSlot(entrevista.id)}
              title={t("pages.entrevistas.remover")}
            >
              ✕
            </button>
          )}
          {renderContactButton(entrevista.interviewer_user_id)}
        </span>
      );
    }

    if (hasPermission("shift.assign_any_interviewer")) {
      return (
        <button
          type="button"
          className="inscrever-btn"
          onClick={() => setAssignModal({ escalaId: entrevista.id, role: "interviewer", dataHorario: dateLabel })}
        >
          {t("pages.entrevistas.atribuir")}
        </button>
      );
    }

    return (
      <button
        type="button"
        className={`inscrever-btn ${!canSelfEnroll(INTERVIEWER_CAPABILITY_NAMES) ? "is-disabled" : ""}`}
        disabled={!canSelfEnroll(INTERVIEWER_CAPABILITY_NAMES)}
        onClick={() => setSelfEnrollModal({ escalaId: entrevista.id, role: "interviewer", funcao: t("roles.interviewer"), dataHorario: dateLabel })}
      >
        {t("pages.entrevistas.inscreverButton")}
      </button>
    );
  };

  const renderAssistantCell = (entrevista: EscalaPlantao) => {
    const dateLabel = formatDateTime(entrevista?.start_date_utc, t("pages.configuration.dateTimeFormats.long"), { timezone, locale: t("current_locale") });

    if (entrevista.assistant_user_id) {
      const name = `${entrevista.assistant_user_id.first_name ?? ""} ${entrevista.assistant_user_id.last_name ?? ""} (${entrevista.assistant_user_id.cod_curso ?? ""})`;
      return (
        <span className="slot-filled">
          {name}
          {hasPermission("shift.remove_any_assistant") && (
            <button
              type="button"
              className="remove-slot-btn"
              onClick={() => removeAssistantFromSlot(entrevista.id)}
              title={t("pages.entrevistas.remover")}
            >
              ✕
            </button>
          )}
          {renderContactButton(entrevista.assistant_user_id)}
        </span>
      );
    }

    if (hasPermission("shift.assign_any_assistant")) {
      return (
        <button
          type="button"
          className="inscrever-btn"
          onClick={() => setAssignModal({ escalaId: entrevista.id, role: "assistant", dataHorario: dateLabel })}
        >
          {t("pages.entrevistas.atribuir")}
        </button>
      );
    }

    return (
      <button
        type="button"
        className={`inscrever-btn ${!canSelfEnroll(ASSISTANT_CAPABILITY_NAMES) ? "is-disabled" : ""}`}
        disabled={!canSelfEnroll(ASSISTANT_CAPABILITY_NAMES)}
        onClick={() => setSelfEnrollModal({ escalaId: entrevista.id, role: "assistant", funcao: t("roles.assistant"), dataHorario: dateLabel })}
      >
        {t("pages.entrevistas.inscreverButton")}
      </button>
    );
  };

  const columns: TableColumn<EscalaPlantao>[] = [
    {
      id: "inscrever",
      header: t("roles.interviewer"),
      render: renderInterviewerCell
    },
    {
      id: "funcao",
      header: t("roles.assistant"),
      render: renderAssistantCell
    },
    {
      id: "dataHorario",
      header: t("pages.entrevistas.dataHorario"),
      render: (entrevista) => {
        const startLabel = formatDateTime(
          entrevista?.start_date_utc,
          t("pages.configuration.dateTimeFormats.long"),
          { timezone: timezone, locale: t("current_locale") }
        );

        if (!entrevista?.end_date_utc) {
          return startLabel;
        }

        const endLabel = formatDateTime(
          entrevista.end_date_utc,
          t("pages.configuration.dateTimeFormats.long"),
          { timezone: timezone, locale: t("current_locale") }
        );

        return `${startLabel} - ${endLabel}`;
      }
    },
    {
      id: "situacao",
      header: t("pages.entrevistas.situacao"),
      render: () => getShiftStatusLabel(plantao?.status)
    },
    {
      id: "contato",
      header: t("pages.entrevistas.contato"),
      render: (entrevista) => <button>{t("pages.entrevistas.solicitarContato")}</button>
    },
    {
      id: "facilitador",
      header: t("pages.entrevistas.facilitador"),
      render: () => `${plantao?.facilitator_user_id?.first_name ?? ""} ${plantao?.facilitator_user_id?.last_name ?? ""}`
    }
  ];

  return (
    <div className="entrevistas-page">
      <TopMenu />

      <main className="entrevistas-content">
        <section className="entrevistas-hero">
          <p className="entrevistas-hero-title">{titulo}</p>
          <p className="entrevistas-hero-subtitle">
            {t("pages.entrevistas.subtitle")}
          </p>
        </section>

        <div className="shift-info-bar">
          <div className="shift-info-item">
            <span className="shift-info-label">{t("pages.entrevistas.situacao")}</span>
            <span className="shift-info-value">{plantao?.status ? t(plantao.status.toUpperCase()) : "—"}</span>
          </div>
          <div className="shift-info-item">
            <span className="shift-info-label">{t("pages.entrevistas.modalidade")}</span>
            <span className="shift-info-value">{modalidadeDisplay}</span>
          </div>
          {!isOnlineShift && (
            <div className="shift-info-item">
              <span className="shift-info-label">{t("pages.entrevistas.local")}</span>
              <span className="shift-info-value">{localDoPlantao || "—"}</span>
            </div>
          )}
          <div className="shift-info-item">
            <span className="shift-info-label">{t("pages.entrevistas.facilitador")}</span>
            <span className="shift-info-value">
              <FacilitatorCell facilitator={plantao?.facilitator_user_id ?? null} />
            </span>
          </div>
        </div>

        <Table<EscalaPlantao>
          beforeHeader={
            <div className="entrevistas-topbar">{t("pages.entrevistas.entrevistadorAssistente")}</div>
          }
          bodyRowClassName="entrevistas-row"
          columns={columns}
          data={plantao?.shift_team_shift ? plantao?.shift_team_shift.sort((a, b) => new Date(a.start_date_utc).getTime() - new Date(b.start_date_utc).getTime()) : []}
          getRowKey={(entrevista) => entrevista?.id}
          gridTemplateColumns="minmax(110px, 1.2fr) minmax(150px, 1.2fr) minmax(180px, 3.6fr)"
          headerRowClassName="entrevistas-row entrevistas-header"
          stickyHeaderTop={44}
          wrapperClassName="entrevistas-table"
        />

        <button
          type="button"
          className="back-button"
          onClick={() => window.history.back()}
        >
          {t("pages.entrevistas.voltar")}
        </button>
      </main>

      {selfEnrollModal && (
        <Modal
          closeOnOverlayClick={true}
          contentClassName="entrevistas-modal-content"
          isOpen={true}
          onRequestClose={() => setSelfEnrollModal(null)}
          showCloseButton={true}
        >
          <p>
            {t("pages.entrevistas.confirmMessage", { funcao: selfEnrollModal.funcao, data: selfEnrollModal.dataHorario })}
          </p>
          <button type="button" onClick={handleSelfEnrollConfirm}>
            {t("pages.entrevistas.simConfirmo")}
          </button>
        </Modal>
      )}

      {assignModal && (
        <Modal
          closeOnOverlayClick={true}
          contentClassName="entrevistas-modal-content entrevistas-assign-modal"
          isOpen={true}
          onRequestClose={() => setAssignModal(null)}
          showCloseButton={true}
        >
          <p className="assign-modal-title">
            {t("pages.entrevistas.atribuirTitulo", {
              role: assignModal.role === "interviewer" ? t("roles.interviewer") : t("roles.assistant"),
              data: assignModal.dataHorario
            })}
          </p>
          <input
            type="text"
            className="assign-search-input"
            placeholder={t("pages.entrevistas.buscarUsuario")}
            value={userSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            autoFocus
          />
          <ul className="assign-user-list">
            {searchLoading && (
              <li className="assign-no-results">{t("pages.entrevistas.buscando")}</li>
            )}
            {!searchLoading && modalCandidates.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className="assign-user-btn"
                  onClick={() => handleAssignConfirm(u)}
                >
                  {u.first_name} {u.last_name}
                  {u.cod_curso ? ` (${u.cod_curso})` : ""}
                </button>
              </li>
            ))}
            {!searchLoading && modalCandidates.length === 0 && (
              <li className="assign-no-results">{t("pages.entrevistas.nenhumUsuario")}</li>
            )}
          </ul>
        </Modal>
      )}

      {contactModal && (
        <ContactInfoModal
          userId={contactModal.userId}
          userName={contactModal.userName}
          isOpen={true}
          onClose={() => setContactModal(null)}
        />
      )}
    </div>
  );
}


